/**
 * @fileoverview
 * Homepage section design system primitives.
 *
 * These components provide consistent section-level layout patterns
 * used across the homepage to reduce repetition and improve maintainability.
 *
 * @module homepage/primitives
 */

import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Props for the SectionContainer primitive.
 * Provides the standard max-width container with responsive padding.
 */
export type SectionContainerProps = {
  /** Child content */
  children: ReactNode;
  /** Additional CSS classes */
  className?: string;
};

/**
 * SectionContainer renders the standard max-width container with responsive padding.
 * Used by all homepage sections for consistent width and padding.
 *
 * @example
 * ```tsx
 * <SectionContainer>
 *   <h2>Section Content</h2>
 * </SectionContainer>
 * ```
 */
export function SectionContainer({ children, className = "" }: SectionContainerProps) {
  return (
    <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`.trim()}>
      {children}
    </div>
  );
}

/**
 * Props for the SectionEyebrow primitive.
 */
export type SectionEyebrowProps = {
  /** The label content overlaid on the decorative line */
  label: ReactNode;
  /** Additional CSS classes for the container */
  className?: string;
  /** Alignment - defaults to center */
  align?: "left" | "center" | "right";
  /** Background color for the label chip covering the line */
  backgroundClassName?: string;
  /** Whether to show the decorative horizontal line. Defaults to true. */
  showLine?: boolean;
};

/**
 * SectionEyebrow renders the decorative horizontal lines with label pattern.
 * Used for section subtitles across the homepage.
 *
 * @example
 * ```tsx
 * <SectionEyebrow label="Pricing" />
 * <SectionEyebrow label="Features" align="left" />
 * ```
 */
export function SectionEyebrow({
  label,
  className = "",
  align = "center",
  backgroundClassName = "bg-gray-50",
  showLine = true,
}: SectionEyebrowProps) {
  const alignClasses = {
    left: "left-0 pr-5",
    center: "left-1/2 -translate-x-1/2 px-5",
    right: "right-0 pl-5",
  };

  return (
    <div className={`relative mb-4 min-h-5 w-full ${className}`.trim()}>
      {showLine && <div className="absolute inset-x-0 top-[10px] h-px bg-[#e6e6e6]" />}
      <span
        className={`font-homepage absolute top-0 z-[1] text-[14px] font-medium leading-[140%] text-gray-600 ${alignClasses[align]} ${backgroundClassName}`.trim()}
      >
        {label}
      </span>
    </div>
  );
}

/**
 * Props for the SectionHeader primitive.
 */
export type SectionHeaderProps = {
  /** The eyebrow label (optional) */
  eyebrow?: ReactNode;
  /** The main section title */
  title: ReactNode;
  /** Additional CSS classes for the container */
  className?: string;
  /** Eyebrow alignment */
  eyebrowAlign?: "left" | "center" | "right";
};

/**
 * SectionHeader renders a complete section header with optional eyebrow and title.
 * Provides consistent spacing and typography for section headers.
 *
 * @example
 * ```tsx
 * <SectionHeader eyebrow="Pricing" title="Choose Your Plan" />
 * <SectionHeader title="Simple Features" eyebrowAlign="left" />
 * ```
 */
export function SectionHeader({
  eyebrow,
  title,
  className = "",
  eyebrowAlign = "center",
}: SectionHeaderProps) {
  return (
    <div className={`mb-12 text-center ${className}`.trim()}>
      {eyebrow && <SectionEyebrow label={eyebrow} align={eyebrowAlign} />}
      <h2 className="text-2xl font-bold leading-tight text-gray-900 sm:text-3xl md:text-4xl lg:text-[2.5rem]">
        {title}
      </h2>
    </div>
  );
}

/**
 * Props for the SectionShell primitive.
 */
export type SectionShellProps = {
  /** Child content */
  children: ReactNode;
  /** Section ID for anchor navigation */
  id?: string;
  /** ARIA label for accessibility */
  ariaLabel: string;
  /** Additional CSS classes */
  className?: string;
  /** Background color - defaults to gray-50 */
  variant?: "default" | "dark" | "white";
  /** Data test ID for testing */
  "data-testid"?: string;
  /** Disable default padding when section has its own padding */
  noPadding?: boolean;
};

/**
 * SectionShell provides the complete wrapper for a homepage section.
 * Combines section element with consistent padding and optional container.
 *
 * @example
 * ```tsx
 * <SectionShell id="pricing" ariaLabel="Pricing section">
 *   <SectionHeader eyebrow="Pricing" title="Choose Your Plan" />
 *   <p>Content goes here</p>
 * </SectionShell>
 * ```
 */
export function SectionShell({
  children,
  id,
  ariaLabel,
  className = "",
  variant = "default",
  "data-testid": dataTestId,
  noPadding = false,
}: SectionShellProps) {
  const variantClasses = {
    default: "bg-gray-50",
    dark: "bg-dark-page",
    white: "bg-white",
  };

  const paddingClasses = noPadding ? "" : "py-8 sm:py-10 lg:py-16 xl:py-20";

  return (
    <section
      id={id}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      className={`relative w-full ${paddingClasses} ${variantClasses[variant]} ${className}`.trim()}
    >
      {children}
    </section>
  );
}

export type PageHeroBackgroundImage = {
  src: string;
  alt?: string;
  className?: string;
  priority?: boolean;
};

export type PageHeroCta = {
  text: string;
  href: string;
};

export type PageHeroProps = {
  title: ReactNode;
  subtitle: ReactNode;
  eyebrow?: ReactNode;
  cta?: PageHeroCta;
  children?: ReactNode;
  id?: string;
  ariaLabel?: string;
  className?: string;
  containerClassName?: string;
  contentClassName?: string;
  textContentClassName?: string;
  childrenClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  eyebrowClassName?: string;
  eyebrowVariant?: "text" | "pill";
  ctaClassName?: string;
  backgroundImage?: PageHeroBackgroundImage;
  "data-testid"?: string;
};

/**
 * PageHero encapsulates the shared dark marketing hero pattern used across subpages.
 * It provides the background image/overlay, eyebrow, heading, subtitle, and optional CTA.
 */
export function PageHero({
  title,
  subtitle,
  eyebrow,
  cta,
  children,
  id,
  ariaLabel = "Hero",
  className,
  containerClassName,
  contentClassName,
  textContentClassName,
  childrenClassName,
  titleClassName,
  subtitleClassName,
  eyebrowClassName,
  eyebrowVariant = "text",
  ctaClassName,
  backgroundImage,
  "data-testid": dataTestId,
}: PageHeroProps) {
  const eyebrowBaseClassName =
    eyebrowVariant === "pill"
      ? "inline-flex items-center rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white/90"
      : "inline-block text-sm font-medium uppercase tracking-widest text-gold-accent";

  const hasSupportingContent = Boolean(cta) || Boolean(children);

  return (
    <SectionShell
      id={id}
      ariaLabel={ariaLabel}
      variant="dark"
      noPadding
      data-testid={dataTestId}
      className={cn("relative overflow-hidden pt-24 pb-16 md:pt-32 md:pb-20", className)}
    >
      {backgroundImage ? (
        <div className="absolute inset-0 overflow-hidden">
          <Image
            src={backgroundImage.src}
            alt={backgroundImage.alt ?? ""}
            fill
            className={cn("object-cover object-center opacity-40", backgroundImage.className)}
            priority={backgroundImage.priority ?? true}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
        </div>
      ) : null}

      <SectionContainer className={cn("relative z-10", containerClassName)}>
        <div
          className={cn(
            hasSupportingContent && "flex flex-col items-center gap-10",
            contentClassName,
          )}
        >
          <div className={cn("mx-auto max-w-3xl text-center", textContentClassName)}>
            {eyebrow ? (
              <span className={cn("mb-6", eyebrowBaseClassName, eyebrowClassName)}>{eyebrow}</span>
            ) : null}

            <h1
              className={cn(
                "text-balance text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-[56px]",
                titleClassName,
              )}
            >
              {title}
            </h1>

            {subtitle ? (
              <p
                className={cn(
                  "mx-auto mt-6 max-w-2xl text-pretty text-base leading-relaxed text-white/80 md:text-lg",
                  subtitleClassName,
                )}
              >
                {subtitle}
              </p>
            ) : null}

            {cta ? (
              <div className="mt-10">
                <Link
                  href={cta.href}
                  className={cn(
                    "inline-flex items-center justify-center rounded-lg bg-white px-8 py-3.5 text-base font-medium text-[#1a1a2e] transition-colors hover:bg-gray-100",
                    ctaClassName,
                  )}
                >
                  {cta.text}
                </Link>
              </div>
            ) : null}
          </div>

          {children ? <div className={cn("w-full", childrenClassName)}>{children}</div> : null}
        </div>
      </SectionContainer>
    </SectionShell>
  );
}
